// src/components/HistoryManagement.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box, CircularProgress, Grid, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Card, CardContent, useTheme, useMediaQuery, } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

// Base API URL
const path = 'http://192.168.1.248:3103';

// Helper function to format ISO date strings to dd-mm-yyyy
const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const HistoryManagement = ({ open, onClose, inquiryId }) => {
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState(null);
    const [followupData, setFollowupData] = useState([]);

    // Responsive design: determine if device is desktop (lg and above)
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    // Fetch inquiry master data
    useEffect(() => {
        if (open && inquiryId) {
            setLoading(true);
            axios
                .get(`${path}/api/getInquiryHistory`, { params: { inquiryId } })
                .then((res) => {
                    // Assuming the API returns the record in res.data.data
                    setHistoryData(res.data.data);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching inquiry history:", error);
                    setLoading(false);
                });
        }
    }, [open, inquiryId]);

    // Fetch inquiry follow-up records
    useEffect(() => {
        if (open && inquiryId) {
            axios
                .get(`${path}/api/getInquiryFollowup`, { params: { inquiryId } })
                .then((res) => {
                    // Assuming the API returns an array of follow-up records in res.data.data
                    setFollowupData(res.data.data);
                })
                .catch((error) => {
                    console.error("Error fetching inquiry followup records:", error);
                });
        }
    }, [open, inquiryId]);

    // Render follow-up activity in table format for desktop devices with smaller text
    const renderFollowupTable = () => (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
                <TableHead sx={{ backgroundColor: "grey.300" }}>
                    <TableRow>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>S.No</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Next Follow-Up Date</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Lead Source</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Employee</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Status</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Requirement</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Remark</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Qty</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Rate</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>Amount</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {followupData.map((record, index) => (
                        <TableRow key={record.followupId}>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{index + 1}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{formatDate(record.nextFollowUpDate)}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.leadSourceName}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.employeeName}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.statusName}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.requirement}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.remark}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.qty}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.rate}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem" }}>{record.amount}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Render follow-up activity in card format for small/medium devices with smaller text
    const renderFollowupCards = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {followupData.map((record, index) => (
                <Card key={record.followupId} variant="outlined">
                    <CardContent>
                        <Typography variant="caption" display="block" gutterBottom>
                            S.No: {index + 1}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Next Follow-Up:</strong> {formatDate(record.nextFollowUpDate)}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Lead Source:</strong> {record.leadSourceName}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Employee:</strong> {record.employeeName}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Status:</strong> {record.statusName}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Requirement:</strong> {record.requirement}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Remark:</strong> {record.remark}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Qty:</strong> {record.qty}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Rate:</strong> {record.rate}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Amount:</strong> {record.amount}
                        </Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={isDesktop ? "lg" : "sm"} PaperProps={{ sx: { width: isDesktop ? "900px" : "100%" } }} >
            <DialogTitle>Inquiry History</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2, }} >
                    {/* Corporate-styled History image/icon */}
                    <HistoryIcon sx={{ fontSize: 40, color: "primary.main", mb: 2 }} />
                    {loading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "150px", }} >
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Loading...
                            </Typography>
                        </Box>
                    ) : historyData ? (
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Number:</strong> {historyData?.inquiryNumber || "N/A"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Date:</strong> {formatDate(historyData?.inquiryDate)} </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Next Follow-Up:</strong> {formatDate(historyData?.nextFollowUpDate)} </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Lead Source Name:</strong> {historyData?.leadSourceName || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Employee Name:</strong> {historyData?.employeeName || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Status:</strong> {historyData?.statusName || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Priority:</strong> {historyData?.priority || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Requirement:</strong> {historyData?.requirement || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Remark:</strong> {historyData?.remark || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Qty:</strong> {historyData?.qty || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Rate:</strong> {historyData?.rate || "N/A"} </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}> <strong>Amount:</strong> {historyData?.amount || "N/A"} </Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: "0.85rem" }}> No history data found for inquiry ID: {inquiryId} </Typography>
                    )}
                </Box>
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" align="center" gutterBottom sx={{ fontSize: "0.85rem" }}> Follow-Up Activity </Typography>
                    {followupData && followupData.length > 0 ? (isDesktop ? renderFollowupTable() : renderFollowupCards()
                    ) : (
                        <Typography variant="body1" align="center" sx={{ fontSize: "0.85rem" }}> No previous activity </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={onClose} sx={{ fontSize: "0.75rem" }}> Close </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HistoryManagement;